const SUBIFY_ONE_TIME_ID = 'subify-widget-one-time-purchase-text';
const SUBIFY_SUBSCRIPTION_ID = 'subify-widget-subscription-text';

class ProductBundlePicker extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #subifyObserver;

  /** @type {((event: Event) => void) | undefined} */
  #subifyClickHandler;

  /** @type {string} */
  #lastBundleQuantity = '1';

  connectedCallback() {
    this.input = this.querySelector('[data-bundle-quantity-input]');
    this.options = this.querySelectorAll('[data-bundle-option]');

    this.options.forEach((option) => {
      option.addEventListener('click', () => this.#select(option));
    });

    const defaultQty = this.dataset.defaultQuantity || '1';
    this.#lastBundleQuantity = defaultQty;
    const defaultOption =
      this.querySelector(`[data-bundle-option][data-quantity="${defaultQty}"]`) || this.options[0];
    if (defaultOption) this.#select(defaultOption, { silent: true });

    this.#waitForSubify();
  }

  disconnectedCallback() {
    this.#subifyObserver?.disconnect();
    this.#subifyObserver = undefined;

    if (this.#subifyClickHandler) {
      document.removeEventListener('click', this.#subifyClickHandler, true);
      this.#subifyClickHandler = undefined;
    }
  }

  #waitForSubify() {
    if (document.getElementById(SUBIFY_ONE_TIME_ID)) {
      this.#initSubifySync();
      return;
    }

    const observer = new MutationObserver(() => {
      if (!document.getElementById(SUBIFY_ONE_TIME_ID)) return;
      observer.disconnect();
      this.#initSubifySync();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  #initSubifySync() {
    const oneTimeEl = document.getElementById(SUBIFY_ONE_TIME_ID);
    if (!oneTimeEl) return;

    const observeTargets = this.#getSubifyObserveTargets();
    if (observeTargets.length === 0) return;

    const sync = () => this.#syncSubifyVisibility();

    this.#subifyObserver = new MutationObserver(sync);
    for (const target of observeTargets) {
      this.#subifyObserver.observe(target, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true,
      });
    }

    this.#subifyClickHandler = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (
        target.closest('.subify-widget-box') ||
        target.closest(`#${SUBIFY_ONE_TIME_ID}`) ||
        target.closest(`#${SUBIFY_SUBSCRIPTION_ID}`) ||
        target.id === SUBIFY_ONE_TIME_ID ||
        target.id === SUBIFY_SUBSCRIPTION_ID
      ) {
        requestAnimationFrame(sync);
      }
    };

    document.addEventListener('click', this.#subifyClickHandler, true);
    sync();
  }

  /** @returns {Element[]} */
  #getSubifyObserveTargets() {
    const targets = new Set();

    for (const id of [SUBIFY_ONE_TIME_ID, SUBIFY_SUBSCRIPTION_ID]) {
      const textEl = document.getElementById(id);
      if (!textEl) continue;

      const label = textEl.closest('.checkbox-label');
      const wrapper = textEl.closest('.subify-choice-box-wrapper');
      const choiceWrapper = textEl.closest('.subify-choice-wrapper');

      if (label) targets.add(label);
      if (wrapper) targets.add(wrapper);
      if (choiceWrapper) targets.add(choiceWrapper);
      targets.add(textEl);
    }

    const widget = document.querySelector('.subify-widget-box');
    if (widget) targets.add(widget);

    return [...targets];
  }

  /**
   * One-time selected when #subify-widget-one-time-purchase-text is inside an active choice.
   * @returns {boolean | null} null when Subify widget is not on the page
   */
  #isSubifyOneTimeSelected() {
    const oneTimeEl = document.getElementById(SUBIFY_ONE_TIME_ID);
    if (!oneTimeEl) return null;

    if (this.#subifyChoiceIsActive(oneTimeEl)) return true;

    const subscriptionEl = document.getElementById(SUBIFY_SUBSCRIPTION_ID);
    if (subscriptionEl && this.#subifyChoiceIsActive(subscriptionEl)) return false;

    return true;
  }

  /** @param {HTMLElement} textEl */
  #subifyChoiceIsActive(textEl) {
    const label = textEl.closest('.checkbox-label');
    const wrapper = textEl.closest('.subify-choice-box-wrapper');

    return Boolean(label?.classList.contains('active') || wrapper?.classList.contains('active'));
  }

  #syncSubifyVisibility() {
    const isOneTime = this.#isSubifyOneTimeSelected();
    if (isOneTime === null) return;

    this.classList.toggle('product-bundle-picker--subify-hidden', !isOneTime);
    this.toggleAttribute('hidden', !isOneTime);

    if (isOneTime) {
      const option =
        this.querySelector(`[data-bundle-option][data-quantity="${this.#lastBundleQuantity}"]`) ||
        this.querySelector('[data-bundle-option].is-selected') ||
        this.options[0];
      if (option) this.#select(/** @type {HTMLElement} */ (option), { silent: true });
      return;
    }

    this.#setQuantity('1', { silent: true });
  }

  /**
   * @param {string} quantity
   * @param {{ silent?: boolean }} opts
   */
  #setQuantity(quantity, opts = {}) {
    if (this.input) this.input.value = quantity;

    document.querySelectorAll('[data-sticky-qty-shadow]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = quantity;
    });
    document.querySelectorAll('[data-sticky-qty] input[type="number"]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = quantity;
    });

    if (!opts.silent) {
      this.dispatchEvent(
        new CustomEvent('bundle-quantity-change', {
          bubbles: true,
          detail: { quantity: Number(quantity) },
        })
      );
    }
  }

  /**
   * @param {HTMLElement} option
   * @param {{ silent?: boolean }} opts
   */
  #select(option, opts = {}) {
    const quantity = option.dataset.quantity;
    if (!quantity) return;

    this.options.forEach((el) => {
      const selected = el === option;
      el.classList.toggle('is-selected', selected);
      el.setAttribute('aria-checked', selected ? 'true' : 'false');
    });

    this.#lastBundleQuantity = quantity;
    this.#setQuantity(quantity, { silent: true });

    if (!opts.silent) {
      this.dispatchEvent(
        new CustomEvent('bundle-quantity-change', {
          bubbles: true,
          detail: { quantity: Number(quantity) },
        })
      );
    }
  }
}

if (!customElements.get('product-bundle-picker')) {
  customElements.define('product-bundle-picker', ProductBundlePicker);
}
